import { JOB_STATUS_CONFIG } from '@/domains/jobs';
import type { Job } from '@/domains/jobs/components/JobCard';
import { NoTasksNotice } from '@/domains/jobs/components/NoTasksNotice';
import { formatDateTime } from '@/shared/lib/date-utils';
import { formatMinutesCompact } from '@/shared/lib/duration-utils';
import type { JobPriority } from '@/shared/lib/priority';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  AlertCircle,
  Calendar,
  Clock,
  ExternalLink,
  MapPin,
  ShieldCheck,
  User,
} from 'lucide-react';
import Map, { Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMemo } from 'react';

interface JobDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job | null;
  onViewFullDetails?: (jobId: string) => void;
  onViewFieldWorker?: (jobId: string) => void;
}

const priorityConfig: Record<JobPriority, { label: string; color: string }> = {
  critical: { label: 'Critical Priority', color: 'text-red-700 dark:text-red-300 font-semibold' },
  high: { label: 'High Priority', color: 'text-red-600 dark:text-red-400' },
  medium: { label: 'Medium Priority', color: 'text-yellow-600 dark:text-yellow-400' },
  low: { label: 'Low Priority', color: 'text-blue-600 dark:text-blue-400' },
  unknown: { label: 'Unknown Priority', color: 'text-gray-500 dark:text-gray-400' },
};

const MAPBOX_TOKEN =
  import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || import.meta.env.VITE_MAPBOX_TOKEN || '';

function ScheduledDateTime({ startTime, scheduledDate }: Pick<Job, 'startTime' | 'scheduledDate'>) {
  const label = resolveScheduledLabel(startTime, scheduledDate);
  return <p className="text-sm text-foreground">{label}</p>;
}

function resolveScheduledLabel(startTime?: string, scheduledDate?: string): string {
  if (startTime) return formatDateTime(startTime);
  if (scheduledDate) {
    return new Date(scheduledDate).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
  return 'Not scheduled';
}

export function JobDetailsModal({
  open,
  onOpenChange,
  job,
  onViewFullDetails,
}: JobDetailsModalProps) {
  const coords = useMemo(() => {
    if (!job?.coordinates) return null;
    const parts = job.coordinates.split(',').map((s) => parseFloat(s.trim()));
    if (parts.length === 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
      if (Math.abs(parts[0]) <= 90) return { lat: parts[0], lng: parts[1] };
      return { lat: parts[1], lng: parts[0] };
    }
    return null;
  }, [job?.coordinates]);

  if (!job) return null;

  const currentDuration = job.duration ?? (job.estimatedHours ?? 4) * 60;

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0">
        {/* Header */}
        <DialogHeader className="space-y-1 border-b border-border p-5 pr-12 text-left">
          <DialogTitle className="text-lg font-semibold text-foreground">{job.title}</DialogTitle>
          {job.description ? (
            <DialogDescription className="line-clamp-2 text-sm text-muted-foreground">
              {job.description}
            </DialogDescription>
          ) : (
            <DialogDescription className="sr-only">Job details</DialogDescription>
          )}
          {job.client && <p className="mt-1 text-xs text-muted-foreground">Client: {job.client}</p>}
        </DialogHeader>

        {/* Content - Two columns */}
        <div className="grid grid-cols-2 gap-6 p-5">
          {/* Left Column - Job Info */}
          <div className="space-y-4">
            {/* Status and Priority Badges */}
            <div className="flex items-center gap-2">
              <Badge className={JOB_STATUS_CONFIG[job.status].color}>
                {JOB_STATUS_CONFIG[job.status].label}
              </Badge>
              <Badge
                variant="outline"
                className={`border-border ${priorityConfig[job.priority].color}`}
              >
                <AlertCircle className="w-3 h-3 mr-1" />
                {priorityConfig[job.priority].label}
              </Badge>
            </div>

            {/* Self-gates on scheduled + task-less (WRK-5792). */}
            <NoTasksNotice hasTasks={job.hasTasks} isScheduled={job.isScheduled} />

            {/* Key Information */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">Location</p>
                  <p className="text-sm text-foreground">{job.location || 'Not specified'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">Scheduled</p>
                  <ScheduledDateTime startTime={job.startTime} scheduledDate={job.scheduledDate} />
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">Duration</p>
                  <div className="flex items-center">
                    <p className="text-sm text-foreground">
                      {formatMinutesCompact(currentDuration)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">Assignee</p>
                  <p className="text-sm text-foreground">{job.assignee || 'Unassigned'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <ShieldCheck className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground mb-0.5">Permit &amp; Reinstatement</p>
                  {job.permitRequired ||
                  job.reinstatementRequired ||
                  job.permitStatusName ||
                  job.permitReferenceNumber ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {job.permitRequired && (
                        <Badge
                          variant="outline"
                          className="text-orange-600 border-orange-600 gap-1 text-xs"
                        >
                          <ShieldCheck className="size-3" />
                          Permit
                        </Badge>
                      )}
                      {job.reinstatementRequired && (
                        <Badge variant="outline" className="text-blue-600 border-blue-600 text-xs">
                          Reinstatement
                        </Badge>
                      )}
                      {job.permitStatusName && (
                        <span className="text-sm text-foreground">{job.permitStatusName}</span>
                      )}
                      {job.permitReferenceNumber && (
                        <span className="text-sm text-foreground">{job.permitReferenceNumber}</span>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Not required</p>
                  )}
                </div>
              </div>
            </div>

            {job.client && (
              <div className="bg-muted/50 rounded-lg p-3 border border-border">
                <p className="text-xs text-muted-foreground mb-1">Client</p>
                <p className="text-sm font-medium text-foreground">{job.client}</p>
              </div>
            )}

            {job.tags && job.tags.length > 0 && (
              <div className="flex gap-1.5 flex-wrap">
                {job.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs border-border">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Map */}
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium">Job Location</p>
            <div className="rounded-lg overflow-hidden border-2 border-border relative h-[300px]">
              {coords && MAPBOX_TOKEN ? (
                <Map
                  mapboxAccessToken={MAPBOX_TOKEN}
                  initialViewState={{
                    longitude: coords.lng,
                    latitude: coords.lat,
                    zoom: 15,
                  }}
                  style={{ width: '100%', height: '300px' }}
                  mapStyle="mapbox://styles/mapbox/streets-v11"
                >
                  <Marker longitude={coords.lng} latitude={coords.lat} color="#E84A2B" />
                </Map>
              ) : (
                <div className="h-full flex items-center justify-center bg-muted/30">
                  <div className="text-center">
                    <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No coordinates available</p>
                  </div>
                </div>
              )}

              {/* Location label */}
              <div className="absolute bottom-4 left-4 right-4 bg-white dark:bg-gray-800 rounded-lg p-3 shadow-lg border border-border z-10">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#E84A2B] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground truncate">
                      {job.location || 'Location not specified'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Actions */}
        <DialogFooter className="flex flex-row items-center justify-between border-t border-border bg-muted/30 p-5 sm:justify-between">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
          <Button
            onClick={() => {
              handleClose();
              if (onViewFullDetails) {
                onViewFullDetails(job.id);
              }
            }}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            View Full Details
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
